import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter
} from 'firebase/firestore';
import { firestore } from './firebase/firebase';

const SITES_COLLECTION = 'sites';

/**
 * Récupère une liste paginée de sites
 */
export const getSites = async (
  pageSize = 10, 
  lastVisible = null, 
  orderByField = 'updatedAt', // Modifier ici: utiliser updatedAt directement, pas publishInfo.updatedAt
  searchTerm = ''
) => {
  try {
    console.log('Tentative de récupération des sites depuis la collection:', SITES_COLLECTION);
    let sitesQuery;
    
    if (searchTerm) {
      // Recherche par nom de domaine
      sitesQuery = query(
        collection(firestore, SITES_COLLECTION),
        where('publishInfo.domainName', '>=', searchTerm),
        where('publishInfo.domainName', '<=', searchTerm + '\uf8ff'),
        limit(pageSize)
      );
    } else {
      // Liste paginée standard
      if (lastVisible) {
        sitesQuery = query(
          collection(firestore, SITES_COLLECTION),
          orderBy(orderByField), // Utilise updatedAt directement
          startAfter(lastVisible),
          limit(pageSize)
        );
      } else {
        sitesQuery = query(
          collection(firestore, SITES_COLLECTION),
          orderBy(orderByField), // Utilise updatedAt directement
          limit(pageSize)
        );
      }
    }
    
    const snapshot = await getDocs(sitesQuery);
    console.log(`Nombre de sites récupérés: ${snapshot.size}`);
    
    // Mapper les données des sites
    const sites = [];
    
    for (const doc of snapshot.docs) {
      const data = doc.data();
      console.log(`Traitement du site ${doc.id}:`, data);
      
      // Récupérer les dernières statistiques (si disponibles)
      let lastStats = null;
      try {
        const statsQuery = query(
          collection(firestore, `${SITES_COLLECTION}/${doc.id}/stats`),
          orderBy('period', 'desc'),
          limit(1)
        );
        const statsSnapshot = await getDocs(statsQuery);
        console.log(`Stats pour ${doc.id}: ${statsSnapshot.size} documents trouvés`);
        
        if (!statsSnapshot.empty) {
          lastStats = statsSnapshot.docs[0].data();
        }
      } catch (error) {
        console.error(`Erreur lors de la récupération des statistiques pour ${doc.id}:`, error);
      }

      // Créer l'objet site
      const site = {
        id: doc.id,
        userId: data.userId || doc.id, // Changé ici: utiliser userId directement
        bucketName: data.publishInfo?.bucketName || '',
        domainName: data.publishInfo?.domainName || 'Non défini',
        publishId: data.publishInfo?.publishId || '',
        publishedAt: data.publishInfo?.publishedAt || null,
        updatedAt: data.updatedAt || null, // Changé ici: utiliser updatedAt directement
        lastVisitCount: lastStats?.total || 0,
        status: data.publishInfo?.domainName ? 'published' : 'draft'
      };
      
      sites.push(site);
      console.log(`Site ${doc.id} traité:`, site);
    }
    
    const newLastVisible = snapshot.docs.length > 0
      ? snapshot.docs[snapshot.docs.length - 1]
      : null;
    
    console.log(`Retour de ${sites.length} sites`);
    return { sites, lastVisible: newLastVisible };
  } catch (error) {
    console.error('Erreur lors de la récupération des sites:', error);
    return { sites: [], lastVisible: null };
  }
};

/**
 * Récupère les détails d'un site spécifique par ID
 */
export const getSiteById = async (siteId) => {
  try {
    const siteDoc = await getDoc(doc(firestore, SITES_COLLECTION, siteId));
    
    if (!siteDoc.exists()) {
      throw new Error('Site non trouvé');
    }
    
    const data = siteDoc.data();
    
    // Récupérer les statistiques mensuelles et annuelles du site
    const statsQuery = query(
      collection(firestore, `${SITES_COLLECTION}/${siteId}/stats`),
      orderBy('period', 'desc'),
      limit(12)
    );
    const statsSnapshot = await getDocs(statsQuery);
    const stats = statsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Récupérer les 20 dernières visites
    const visitsQuery = query(
      collection(firestore, `${SITES_COLLECTION}/${siteId}/visits`),
      orderBy('timestamp', 'desc'),
      limit(20)
    );
    const visitsSnapshot = await getDocs(visitsQuery);
    const visits = visitsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return {
      id: siteDoc.id,
      userId: data.publishInfo?.userId || siteDoc.id,
      bucketName: data.publishInfo?.bucketName || '',
      domainName: data.publishInfo?.domainName || 'Non défini',
      publishId: data.publishInfo?.publishId || '',
      publishedAt: data.publishInfo?.publishedAt || null,
      updatedAt: data.publishInfo?.updatedAt || null,
      stats: stats,
      visits: visits,
      status: data.publishInfo?.domainName ? 'published' : 'draft'
    };
  } catch (error) {
    console.error(`Erreur lors de la récupération du site ${siteId}:`, error);
    throw error;
  }
};

/**
 * Met à jour un site
 */
export const updateSite = async (siteId, updateData) => {
  try {
    const siteRef = doc(firestore, SITES_COLLECTION, siteId);
    await updateDoc(siteRef, {
      'publishInfo.domainName': updateData.domainName,
      'publishInfo.updatedAt': new Date()
    });
    return siteId;
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du site ${siteId}:`, error);
    throw error;
  }
};

/**
 * Récupère le nombre total de sites
 */
export const getSiteCount = async () => {
  try {
    const snapshot = await getDocs(collection(firestore, SITES_COLLECTION));
    return snapshot.size;
  } catch (error) {
    console.error('Erreur lors du comptage des sites:', error);
    return 0;
  }
};

/**
 * Récupère les statistiques d'un site (journalières, mensuelles ou annuelles)
 */
export const getSiteStats = async (siteId, type = 'daily') => {
  try {
    const statsQuery = query(
      collection(firestore, `${SITES_COLLECTION}/${siteId}/stats`),
      // Correction: startsWith n'est pas une opération valide pour Firestore
      // Utiliser une condition compatible avec Firestore
      where('period', '>=', type),
      where('period', '<=', type + '\uf8ff'),
      orderBy('period', 'desc'),
      limit(type === 'daily' ? 30 : type === 'monthly' ? 12 : 5)
    );
    
    const statsSnapshot = await getDocs(statsQuery);
    console.log(`Stats pour ${siteId} (${type}): ${statsSnapshot.size} documents trouvés`);
    
    return statsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`Erreur lors de la récupération des statistiques du site ${siteId}:`, error);
    return [];
  }
};

/**
 * Récupère les visites d'un site de façon paginée
 */
export const getSiteVisits = async (siteId, pageSize = 20, lastVisible = null) => {
  try {
    let visitsQuery;
    
    if (lastVisible) {
      visitsQuery = query(
        collection(firestore, `${SITES_COLLECTION}/${siteId}/visits`),
        orderBy('timestamp', 'desc'),
        startAfter(lastVisible),
        limit(pageSize)
      );
    } else {
      visitsQuery = query(
        collection(firestore, `${SITES_COLLECTION}/${siteId}/visits`),
        orderBy('timestamp', 'desc'),
        limit(pageSize)
      );
    }
    
    const snapshot = await getDocs(visitsQuery);
    
    const visits = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const newLastVisible = snapshot.docs.length > 0
      ? snapshot.docs[snapshot.docs.length - 1]
      : null;
    
    return { visits, lastVisible: newLastVisible };
  } catch (error) {
    console.error(`Erreur lors de la récupération des visites du site ${siteId}:`, error);
    return { visits: [], lastVisible: null };
  }
};

/**
 * Supprime complètement un site (infrastructure + Firebase)
 * @param {string} siteId - ID du site à supprimer
 * @param {Object} options - Options de suppression
 * @param {boolean} options.force - Forcer la suppression même en cas d'erreur infrastructure
 * @param {boolean} options.infrastructureOnly - Supprimer uniquement l'infrastructure (garder les données Firebase)
 * @returns {Promise<Object>} Résultat de la suppression
 */
export const deleteSiteComplete = async (siteId, options = {}) => {
  try {
    const { 
      force = false, 
      infrastructureOnly = false,
      userId = null 
    } = options;

    console.log(`Début de la suppression complète du site ${siteId}`, { force, infrastructureOnly, userId });

    // Récupérer les détails du site d'abord pour obtenir l'userId si nécessaire
    let targetUserId = userId;
    if (!targetUserId) {
      console.log('Récupération des détails du site pour obtenir userId...');
      const siteDetails = await getSiteById(siteId);
      targetUserId = siteDetails.userId;
      console.log(`UserId récupéré: ${targetUserId}`);
    }

    const result = {
      siteId,
      infrastructureDeleted: false,
      firebaseDeleted: false,
      errors: [],
      adminRequest: true
    };

    // 1. Supprimer l'infrastructure via l'API du service de déploiement
    try {
      console.log('Appel de l\'API de suppression infrastructure...');
      
      const deploymentServiceUrl = process.env.REACT_APP_DEPLOYMENT_SERVICE_URL;
      const adminApiKey = process.env.REACT_APP_DEPLOYMENT_API_KEY;

      if (!deploymentServiceUrl || !adminApiKey) {
        throw new Error('Configuration manquante: DEPLOYMENT_SERVICE_URL ou DEPLOYMENT_API_KEY non configurés');
      }

      const response = await fetch(`${deploymentServiceUrl}/api/admin/sites/${siteId}${force ? '?force=true' : ''}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminApiKey}`
        },
        body: JSON.stringify({
          userId: targetUserId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Erreur inconnue' }));
        throw new Error(`Erreur API (${response.status}): ${errorData.message || response.statusText}`);
      }

      const infrastructureResult = await response.json();
      console.log('Suppression infrastructure réussie:', infrastructureResult);
      
      result.infrastructureDeleted = true;
      result.infrastructureResult = infrastructureResult;

    } catch (infrastructureError) {
      console.error('Erreur lors de la suppression de l\'infrastructure:', infrastructureError);
      result.errors.push({
        type: 'infrastructure',
        message: infrastructureError.message,
        error: infrastructureError
      });

      if (!force) {
        throw new Error(`Échec de la suppression de l'infrastructure: ${infrastructureError.message}. Utilisez l'option 'force' pour continuer.`);
      }

      console.warn('Mode force activé: poursuite malgré l\'erreur infrastructure');
    }

    // 2. Supprimer les données Firebase (seulement si pas infrastructureOnly)
    if (!infrastructureOnly) {
      try {
        console.log('Suppression des données Firebase...');
        
        // Supprimer le document principal du site
        await deleteSite(siteId);
        
        result.firebaseDeleted = true;
        console.log('Suppression Firebase réussie');

      } catch (firebaseError) {
        console.error('Erreur lors de la suppression Firebase:', firebaseError);
        result.errors.push({
          type: 'firebase',
          message: firebaseError.message,
          error: firebaseError
        });

        if (!force) {
          throw new Error(`Échec de la suppression des données: ${firebaseError.message}`);
        }

        console.warn('Mode force activé: poursuite malgré l\'erreur Firebase');
      }
    } else {
      result.firebaseDeleted = false;
      result.message = 'Suppression infrastructure uniquement (données Firebase conservées)';
    }

    // 3. Déterminer le résultat global
    const hasErrors = result.errors.length > 0;
    const isPartialSuccess = (result.infrastructureDeleted || result.firebaseDeleted) && hasErrors;
    const isCompleteSuccess = result.infrastructureDeleted && result.firebaseDeleted && !hasErrors;

    if (isCompleteSuccess) {
      result.success = true;
      result.message = 'Site supprimé complètement avec succès (infrastructure + données)';
    } else if (isPartialSuccess) {
      result.success = true;
      result.message = 'Suppression partielle réussie (voir détails des erreurs)';
    } else {
      result.success = false;
      result.message = 'Échec de la suppression complète';
    }

    console.log('Résultat final de la suppression:', result);
    return result;

  } catch (error) {
    console.error(`Erreur lors de la suppression complète du site ${siteId}:`, error);
    
    return {
      success: false,
      siteId,
      message: error.message,
      infrastructureDeleted: false,
      firebaseDeleted: false,
      errors: [{
        type: 'general',
        message: error.message,
        error: error
      }],
      adminRequest: true
    };
  }
};

/**
 * Supprime uniquement les données Firebase d'un site
 * (fonction étendue de la suppression existante)
 */
export const deleteSite = async (siteId) => {
  try {
    console.log(`Suppression des données Firebase pour le site: ${siteId}`);
    
    // Pour l'instant, simuler la suppression Firebase
    // En réalité, vous devriez implémenter la suppression du document Firestore
    // et des sous-collections (stats, visits, etc.)
    
    // TODO: Implémenter la suppression réelle Firebase
    // await deleteDoc(doc(firestore, SITES_COLLECTION, siteId));
    
    // Supprimer les sous-collections si nécessaire
    // - sites/{siteId}/stats
    // - sites/{siteId}/visits
    
    console.log(`Données Firebase supprimées pour le site: ${siteId}`);
    return { success: true, siteId };
    
  } catch (error) {
    console.error(`Erreur lors de la suppression Firebase du site ${siteId}:`, error);
    throw error;
  }
};